<template><!-- PWA install prompt host (renderless) --></template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { f7 } from "framework7-vue";
import { Capacitor } from "@capacitor/core";
import { useAuthStore } from "../js/pinia-stores/auth.js";
import i18n from "@/js/i18n.js";

const props = defineProps({
  f7route: Object,
  f7router: Object,
});

const INSTALLED_KEY = "qkUJkct32OVdt";
const DISMISSED_AT_KEY = "qkUJkct32OVdtDismissedAt";
const DISMISS_COUNT_KEY = "qkUJkct32OVdtDismissCount";
const LAST_OFFER_KEY = "qkUJkct32OVdtLastOffer";
// Tiered cooldowns by number of dismissals: 1 day, then 7 days, then 30 days.
const COOLDOWN_TIERS_MS = [24 * 3600 * 1000, 7 * 24 * 3600 * 1000, 30 * 24 * 3600 * 1000];
const OFFER_DELAY_MS = 30000;
const STICKY_CLOSE_TIMEOUT_MS = 3000000;

const authStore = useAuthStore();
const device = computed(() => authStore.device);

const deferredPrompt = ref(null);
const offeredThisSession = ref(false);
const installKey = "avocash:install";
let installToast = null;
let iosTimer = null;

const isIOS = computed(() => {
  if (!device.value) return false;
  const os = String(device.value.os || "").toLowerCase();
  return (
    ["ios", "iphone", "ipad", "macos"].includes(os) ||
    !!device.value.ios ||
    !!device.value.iphone ||
    !!device.value.ipad
  );
});

function isInstalled() {
  return !!window.__piniaLcStorage.get(INSTALLED_KEY);
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    typeof navigator.standalone === "boolean" &&
    navigator.standalone === true
  );
}

function cooldownMs() {
  const count = Number(window.__piniaLcStorage.get(DISMISS_COUNT_KEY) || 0);
  return COOLDOWN_TIERS_MS[Math.min(Math.max(count, 0), COOLDOWN_TIERS_MS.length - 1)];
}

function isDismissCooldownActive() {
  const at = Number(window.__piniaLcStorage.get(DISMISSED_AT_KEY) || 0);
  if (!at) return false;
  return Date.now() - at < cooldownMs();
}

function wasShownRecently() {
  const at = Number(window.__piniaLcStorage.get(LAST_OFFER_KEY) || 0);
  if (!at) return false;
  // Never show more than once per day, even if it was dismissed outside the app.
  return Date.now() - at < 24 * 3600 * 1000;
}

function canOfferPrompt() {
  if (Capacitor.isNativePlatform()) return false;
  if (isStandalone()) return false;
  if (isInstalled()) return false;
  if (isDismissCooldownActive()) return false;
  if (wasShownRecently()) return false;
  if (offeredThisSession.value) return false;
  if (installToast) return false;
  return true;
}

function recordOfferShown() {
  window.__piniaLcStorage.set(LAST_OFFER_KEY, Date.now());
  offeredThisSession.value = true;
}

function recordDismissal() {
  const count = Number(window.__piniaLcStorage.get(DISMISS_COUNT_KEY) || 0) + 1;
  window.__piniaLcStorage.set(DISMISS_COUNT_KEY, count);
  window.__piniaLcStorage.set(DISMISSED_AT_KEY, Date.now());
  deferredPrompt.value = null;
  document.dispatchEvent(new CustomEvent("avocash:install-prompt-dismissed"));
}

function recordInstalled() {
  window.__piniaLcStorage.set(INSTALLED_KEY, true);
  window.__piniaLcStorage.set(DISMISS_COUNT_KEY, 0);
  window.__piniaLcStorage.remove(DISMISSED_AT_KEY);
  window.__piniaLcStorage.remove(LAST_OFFER_KEY);
  deferredPrompt.value = null;
}

function onBeforeInstallPrompt(e) {
  e.preventDefault();
  deferredPrompt.value = e;
  if (canOfferPrompt()) {
    setTimeout(offerPrompt, 2000);
  }
}

function onAppInstalled() {
  recordInstalled();
  if (installToast) {
    installToast.close();
    installToast = null;
  }
  f7.toast.show({ text: i18n.global.t("common.installSuccess"), closeTimeout: 3000 });
}

onMounted(() => {
  if (Capacitor.isNativePlatform()) return;
  if (isStandalone()) return;

  window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  window.addEventListener("appinstalled", onAppInstalled);

  // iOS has no beforeinstallprompt event — offer the "Add to Home Screen"
  // instructions once we're reasonably sure the user is engaged, on a manual
  // cooldown, after a delay.
  if (isIOS.value) {
    iosTimer = setTimeout(() => {
      if (canOfferPrompt()) offerPrompt();
    }, OFFER_DELAY_MS);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
  window.removeEventListener("appinstalled", onAppInstalled);
  if (iosTimer) clearTimeout(iosTimer);
  if (installToast) {
    installToast.destroy();
    installToast = null;
  }
});

function offerPrompt() {
  if (!canOfferPrompt()) return;
  recordOfferShown();
  showInstallPrompt();
}

function handleDismiss() {
  recordDismissal();
}

function showInstallPrompt() {
  if (installToast) return;
  const isIos = isIOS.value;
  const t = i18n.global.t;

  const buttonClasses =
    "button button-small button-outline button-round button-raised";

  const actionButtons = isIos
    ? `<a id="pwa-install-ok-button" class="${buttonClasses} color-primary border-color-primary" style="border-radius:32px;border-width:medium;">${t("common.buttonOk")}</a>`
    : `<a id="pwa-install-button" class="${buttonClasses} color-primary border-color-primary" style="border-radius:32px;border-width:medium;">${t("common.install")}</a>` +
      `<a id="pwa-install-cancel-button" class="${buttonClasses} color-red border-color-red" style="border-radius:32px;border-width:medium;">${t("common.cancel")}</a>`;

  installToast = f7.toast.create({
    destroyOnClose: true,
    text: `<div style="display:flex;flex-direction:column;gap:8px;align-items:center;max-height:45vh;overflow-y:auto;">
      <span>${isIos ? iosInstructionsHtml() : installInstructionsHtml()}</span>
      <span style="display:flex;gap:8px;flex-wrap:wrap;justify-content:center;">${actionButtons}</span>
    </div>`,
    horizontalPosition: "center",
    position: "bottom",
    closeButton: true,
    closeTimeout: STICKY_CLOSE_TIMEOUT_MS,
    on: {
      // Any way the toast goes away (cancel button, swipe, close button,
      // timeout, tap-out) counts as a dismissal so we don't nag on every refresh.
      close: () => {
        if (installToast) {
          recordDismissal();
        }
        installToast = null;
      },
    },
  });
  installToast.open();

  const okButton = document.querySelector("#pwa-install-ok-button");
  okButton?.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    recordDismissal();
    installToast.close();
  });

  const installButton = document.querySelector("#pwa-install-button");
  installButton?.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    installToast.close();
    void runInstall();
  });

  const cancelButton = document.querySelector("#pwa-install-cancel-button");
  cancelButton?.addEventListener("click", (e) => {
    e.preventDefault();
    e.stopPropagation();
    recordDismissal();
    installToast.close();
  });
}

function installInstructionsHtml() {
  return (
    '<span class="cols20_80">' +
    '<span style="margin: auto"><i class="icon margin-right-half"><img src="/512x512.svg" width="24" height="24" alt="avocash icon" class="avocash_logo" /></i></span>' +
    "<span>" +
    '<span class="avocash"><span class="avocash_primary">avo</span><span class="avocash_gold avocash">&cent;</span><span class="avocash_primary">ash</span></span><br />' +
    "<span>" +
    i18n.global.t("common.installInstructions") +
    "</span>" +
    "</span>" +
    "</span>"
  );
}

function iosInstructionsHtml() {
  return (
    '<span class="cols20_80">' +
    '<span style="margin: auto"><i class="icon"><img src="/32x32.svg" width="32" height="32" alt="avocash icon" /></i></span>' +
    '<span class="avocash"><span class="avocash_primary">avo</span><span class="avocash_gold avocash">&cent;</span><span class="avocash_primary">ash</span></span>' +
    "</span><br />" +
    '<div class="list list-outline"><ul>' +
    '<li class="item-content"><div class="item-inner"><div class="item-title">' +
    i18n.global.t("common.installInstructions") +
    "</div></div></li>" +
    '<li class="item-content"><div class="item-inner"><div class="item-title">1. ' +
    i18n.global.t("common.installStep1") +
    " <i class='icon f7-icons color-primary'>square_arrow_up</i></div></div></li>" +
    '<li class="item-content"><div class="item-inner"><div class="item-title">2. ' +
    i18n.global.t("common.installStep2") +
    " <i class='icon f7-icons color-primary'>plus_square</i></div></div></li>" +
    '<li class="item-content"><div class="item-inner"><div class="item-title">3. ' +
    i18n.global.t("common.installStep3") +
    "</div></div></li>" +
    "</ul></div>"
  );
}

async function runInstall() {
  const prompt = deferredPrompt.value;
  deferredPrompt.value = null;
  if (!prompt) {
    recordDismissal();
    return;
  }
  try {
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") {
      recordInstalled();
    } else {
      recordDismissal();
    }
  } catch (err) {
    // prompt() can throw if the engine already disposed the offer
    recordDismissal();
  }
}

// Allow the rest of the app to know whether an install offer is available.
window.getPwaInstallState = function () {
  return {
    canPrompt: !!deferredPrompt.value,
    deferred: !!deferredPrompt.value,
    installed: isInstalled(),
    standalone: isStandalone(),
    native: Capacitor.isNativePlatform(),
    ios: isIOS.value,
    cooldownMs: cooldownMs(),
  };
};
</script>